import { getItem, removeItem, setItem } from './localStorageManagement';

/**
 * Referral capture for the landing site.
 *
 * The landing site (younet.ai) and the portal (younet.ai/portal) are the same
 * origin, so a code stashed in localStorage here is readable verbatim by the
 * signup form over there. That is why capture is a single call in Layout.js
 * rather than appending `?ref=` to the ~15 hardcoded portal sign-up links
 * scattered through this app — those would only carry the code if the visitor
 * clicked one immediately, and would lose it the moment they browsed to
 * another page first.
 *
 * The portal reads this same key (see _helpers/referral.js there) and must
 * keep it in the login-flow allowlist, or an OAuth signup wipes it before the
 * account is created.
 */
export const REFERRAL_STORAGE_KEY = 'referralCode';

/** Query parameters a referral link may use. */
const REFERRAL_PARAMS = ['ref', 'referral', 'referralCode'];

/**
 * Codes are issued from a Crockford-style base32 alphabet, uppercase, 8 chars.
 * Normalising here means a link that has been lowercased by a mail client
 * still attributes, and stops arbitrary query-string junk from being stored
 * and sent to the API later.
 */
export const normalizeReferralCode = (value) => {
  if (typeof value !== 'string') return '';

  const normalized = value.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');

  // Longer than any code we issue — not a referral code.
  if (!normalized || normalized.length > 12) return '';

  return normalized;
};

/**
 * Remember a code that did not arrive as a query parameter.
 *
 * The invite page (/refer/:code) carries the code in the PATH, which
 * captureReferralCode() does not look at — it reads the query string and then
 * strips it, and doing the same to a path segment would delete the page the
 * visitor is standing on.
 *
 * Same last-touch rule as capture: the most recent link wins.
 *
 * @returns {string} the normalised code, or '' if it wasn't one
 */
export const rememberReferralCode = (value) => {
  const code = normalizeReferralCode(value);

  if (code) {
    setItem(REFERRAL_STORAGE_KEY, code);
  }

  return code;
};

/**
 * Drop a stored code that the API has told us will never attribute.
 *
 * ONLY FOR A PROVEN-DEAD CODE. Leaving one in storage means it is still posted
 * at registration, where it silently resolves to no referrer — and worse, it
 * sits on a shared browser waiting to be the next person's non-attribution.
 *
 * Never call this because a lookup FAILED. A failed request tells us nothing
 * about the code, and wiping storage on a network blip would destroy real
 * attribution for everyone who clicked an invite during it.
 */
export const forgetReferralCode = () => removeItem(REFERRAL_STORAGE_KEY);

/**
 * Read a referral code off the current URL and remember it.
 *
 * Last touch wins: if someone arrives through a second person's link, the most
 * recent one is the one that gets credit. That is the usual convention for
 * referral attribution, and re-using the SAME link simply rewrites the same
 * value, so a visitor following their invite twice changes nothing.
 *
 * The parameter is stripped from the address bar afterwards so the code isn't
 * carried into shares, bookmarks or analytics referrers.
 */
export const captureReferralCode = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = REFERRAL_PARAMS.map((key) => params.get(key)).find(Boolean);

    if (!raw) return getItem(REFERRAL_STORAGE_KEY) || '';

    const code = normalizeReferralCode(raw);

    if (code) {
      setItem(REFERRAL_STORAGE_KEY, code);
    }

    REFERRAL_PARAMS.forEach((key) => params.delete(key));

    const query = params.toString();
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`
    );

    return code;
  } catch {
    return '';
  }
};
